import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQuery } from '@apollo/client';
import { Spin, notification } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import { Button, Modal, Information } from 'components/gyramais';
import { formatError, formatString } from 'utils';
import {
  GET_BADESUL_PIX,
  CREATE_BADESUL_PIX,
  CONFIRM_BADESUL_PIX_PAYMENT,
} from './graphql';

// Pagamento da taxa de análise Badesul (0,8% do valor contratado) via PIX
// Banrisul. Espelha o BadesulPixModal do app do cliente; quando o pagamento
// é confirmado, o trigger BadesulPayment move o loan para awaiting-signature.
const BadesulPixModal = ({
  visible,
  onClose,
  loanId,
  refetch,
}) => {
  const [currentPix, setCurrentPix] = useState(null);

  const isExpired = currentPix?.expirationDate
    && new Date(currentPix.expirationDate) < new Date();

  const [createBadesulPix, { loading: createPixLoading }] = useMutation(CREATE_BADESUL_PIX, {
    onError: (error) => notification.error(formatError(error)),
    onCompleted: ({ createBadesulPix }) => {
      setCurrentPix(createBadesulPix);
    },
  });

  const { loading: pixLoading } = useQuery(GET_BADESUL_PIX, {
    variables: {
      loanId,
    },
    skip: !visible || !loanId,
    fetchPolicy: 'cache-and-network',
    onCompleted: ({ badesulPix }) => {
      setCurrentPix(badesulPix);
    },
    onError: (error) => notification.error(formatError(error)),
  });

  const [confirmBadesulPixPayment, { loading: confirmLoading }] = useMutation(
    CONFIRM_BADESUL_PIX_PAYMENT,
    {
      onError: (error) => notification.error(formatError(error)),
      onCompleted: ({ confirmBadesulPixPayment }) => {
        setCurrentPix(confirmBadesulPixPayment);
        if (confirmBadesulPixPayment?.status === 'paid') {
          notification.success({ message: 'Pagamento confirmado!' });
          refetch();
          onClose();
        } else {
          notification.info({
            message: 'Pagamento ainda não identificado.',
            description: 'Assim que o PIX for compensado, o empréstimo seguirá para assinatura.',
          });
        }
      },
    },
  );

  useEffect(() => {
    if (visible && !pixLoading && !currentPix && loanId) {
      createBadesulPix({ variables: { loanId } });
    }
  }, [visible, pixLoading]);

  const loading = pixLoading || createPixLoading || confirmLoading;

  let okButton;
  if (currentPix) {
    okButton = isExpired ? {
      text: 'Gerar novo PIX',
      loading,
      onClick: () => createBadesulPix({ variables: { loanId } }),
    } : {
      text: 'Já efetuei o pagamento',
      loading,
      onClick: () => confirmBadesulPixPayment({ variables: { loanId } }),
    };
  }

  return (
    <Modal
      visible={visible}
      title="Pagamento da taxa de análise via PIX"
      onClose={onClose}
      okButton={okButton}
      cancelButton={currentPix && isExpired ? {
        text: 'Já efetuei o pagamento',
        loading,
        onClick: () => confirmBadesulPixPayment({ variables: { loanId } }),
      } : {
        text: 'Fechar',
        onClick: onClose,
      }}
      className="badesul-pix-modal"
    >
      {loading && !currentPix && <Spin />}

      {currentPix && (
        <>
          <Information
            label="Valor"
            value={formatString(currentPix.amount, 'currency')}
          />
          <Information
            label="Vencimento"
            value={new Date(currentPix.expirationDate).toLocaleString('pt-BR', {
              dateStyle: 'short',
              timeStyle: 'short',
            })}
          />

          {!isExpired ? (
            <>
              <p className="secondary-text" style={{ wordBreak: 'break-all', marginTop: 16 }}>
                {currentPix.pixCode}
              </p>
              <Button
                link
                icon={<CopyOutlined />}
                onClick={() => {
                  navigator.clipboard.writeText(currentPix.pixCode);
                  notification.success({ message: 'Código PIX copiado!' });
                }}
              >
                Copiar código PIX
              </Button>
            </>
          ) : (
            <p style={{ marginTop: 16 }}>
              O PIX expirou. Caso o pagamento já tenha sido efetuado, clique em
              &quot;Já efetuei o pagamento&quot; para atualizar o status.
              Caso contrário, gere um novo código.
            </p>
          )}
        </>
      )}
    </Modal>
  );
};

BadesulPixModal.propTypes = {
  visible: PropTypes.bool,
  onClose: PropTypes.func,
  loanId: PropTypes.string,
  refetch: PropTypes.func,
};

export default BadesulPixModal;
